import { AsyncState, DocumentInfo, Unpacked, UserProfile } from '@borg/types';
import { userElementsStoreFactory } from '@/layers/MyProfile/stores/utils.store';
import { LanguagePatch } from '@/layers/MyProfile/types';

export const useUserProfileStore = defineInitStore('user-profile-store', () => {
  const profile = ref<UserProfile>();
  const profilePdf = ref<DocumentInfo>();
  const initState = ref<AsyncState>('isInitial');
  const updateState = ref<AsyncState>('isInitial');
  const generatePdfState = ref<AsyncState>('isInitial');

  const userLanguagesStore = useUserLanguagesStore();
  const userDriverLicencesStore = useUserDriverLicencesStore();

  async function init() {
    try {
      initState.value = 'inProgress';
      const result = await userProfileService.getProfile();

      if (result.isSuccess) {
        profile.value = result.data;
        userLanguagesStore.setItems(result.data.languages);
        userDriverLicencesStore.setItems(result.data.driverLicences);
        initState.value = 'isSuccess';
      } else {
        initState.value = 'isInvalid';
      }
    } catch (error) {
      initState.value = 'hasError';
    }
  }

  async function update(data: Partial<UserProfile>) {
    try {
      updateState.value = 'inProgress';
      const result = await userProfileService.patchProfile(data);
      if (result.isSuccess) {
        updateState.value = 'isSuccess';
        profile.value = result.data;
      } else {
        updateState.value = 'isInvalid';
      }
    } catch (error) {
      updateState.value = 'hasError';
    }
  }

  async function generatePdf() {
    try {
      generatePdfState.value = 'inProgress';
      profilePdf.value = undefined;
      const result = await userProfileService.getProfilePdf();
      if (result.isSuccess) {
        generatePdfState.value = 'isSuccess';
        profilePdf.value = result.data;
      } else {
        generatePdfState.value = 'isInvalid';
      }
    } catch (error) {
      generatePdfState.value = 'hasError';
    }
  }

  async function checkPublishable() {
    try {
      updateState.value = 'inProgress';
      const result = await userProfileService.getProfilePublishable();
      if (profile.value && result.isSuccess) {
        profile.value = { ...profile.value, publishable: result.data.publishable };
        updateState.value = 'isSuccess';
      } else {
        updateState.value = 'isInvalid';
      }
    } catch (error) {
      updateState.value = 'hasError';
    }
  }

  return {
    profile,
    profilePdf,
    init,
    initState,
    update,
    updateState,
    generatePdf,
    generatePdfState,
    checkPublishable,
  };
});

export const useUserLanguagesStore = userElementsStoreFactory<
  Unpacked<UserProfile['languages']>,
  LanguagePatch
>({
  elementName: 'user-languages',
  manager: userProfileService.languages,
});

export const useUserDriverLicencesStore = userElementsStoreFactory<
  Unpacked<UserProfile['driverLicences']>
>({
  elementName: 'user-driver-licences',
  manager: userProfileService.driverLicences,
});
